import type { ValidationError } from './types';
import { escapeAmpersands } from './utils/sanitize';

/**
 * Result of parsing XML string
 */
export type ParseResult = {
  root: Element | null;
  errors: ValidationError[];
};

/**
 * Parse an XML string into a DOM Element
 *
 * Uses DOMParser in browser environments.
 * For Node.js, this assumes a DOMParser polyfill is available (e.g., xmldom or jsdom).
 *
 * @param xmlString - XML string to parse
 * @returns ParseResult with root element and any parse errors
 */
export const parseXML = (xmlString: string): ParseResult => {
  const errors: ValidationError[] = [];

  if (!xmlString || xmlString.trim() === '') {
    errors.push({
      severity: 'error',
      type: 'parse',
      message: 'XML string is empty',
      element: 'document',
    });
    return { root: null, errors };
  }

  // Get DOMParser (browser native or polyfill)
  const Parser = getDOMParser();
  if (!Parser) {
    errors.push({
      severity: 'error',
      type: 'parse',
      message: 'DOMParser is not available in this environment',
      element: 'document',
    });
    return { root: null, errors };
  }

  const parser = new Parser();
  const sanitizedXml = escapeAmpersands(xmlString);
  const doc = parser.parseFromString(sanitizedXml, 'text/xml');

  // Check for parse errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    errors.push({
      severity: 'error',
      type: 'parse',
      message: parseError.textContent || 'Failed to parse XML',
      element: 'document',
    });
    return { root: null, errors };
  }

  const root = doc.documentElement;
  if (!root) {
    errors.push({
      severity: 'error',
      type: 'parse',
      message: 'No root element found in XML',
      element: 'document',
    });
    return { root: null, errors };
  }

  return { root, errors };
};

/**
 * Get DOMParser from global scope
 * Works in browser and Node.js with polyfill
 */
const getDOMParser = (): typeof DOMParser | null => {
  if (typeof DOMParser !== 'undefined') {
    return DOMParser;
  }

  // Check for global polyfill (e.g., from xmldom)
  if (typeof globalThis !== 'undefined' && (globalThis as unknown as GlobalWithDOM).DOMParser) {
    return (globalThis as unknown as GlobalWithDOM).DOMParser;
  }

  return null;
};

type GlobalWithDOM = {
  DOMParser: typeof DOMParser;
};

/**
 * Get attribute value from element, returning null if not present
 */
export const getAttribute = (element: Element, name: string): string | null => {
  return element.getAttribute(name);
};

/**
 * Get all child elements of a specific tag name
 */
export const getChildElements = (element: Element, tagName?: string): Element[] => {
  const children = Array.from(element.children);
  if (tagName) {
    return children.filter((child) => child.tagName.toLowerCase() === tagName.toLowerCase());
  }
  return children;
};

/**
 * Get all direct child elements (not filtered by tag)
 */
export const getAllChildElements = (element: Element): Element[] => {
  return Array.from(element.children);
};
